import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, Inbox, Loader2, Mail, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toISOString().replace("T", " ").slice(0, 16) + " UTC";
  } catch {
    return iso;
  }
};

export default function AdminSupport() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, items: [], total: 0 });
  const [exporting, setExporting] = useState(false);

  const isAdmin = !!user?.is_admin;

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get("/support/admin/submissions");
      setState({ loading: false, items: data.items || [], total: data.total || 0 });
    } catch (e) {
      setState({ loading: false, items: [], total: 0 });
      toast.error(apiErr(e));
    }
  }, []);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const downloadCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/support/admin/submissions.csv", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "tradecraft-support-submissions.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setExporting(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-8" data-testid="support-admin-forbidden">
        <div className="tc-card p-6 max-w-md">
          <ShieldCheck className="w-6 h-6 text-tc-bear" />
          <h1 className="mt-3 font-heading text-xl font-bold">Admin only</h1>
          <p className="mt-2 text-sm text-tc-sub">
            Support submission access requires a global admin account. Guardian status does not grant access.
          </p>
          <button
            onClick={() => navigate("/dashboard")}
            className="mt-4 text-sm text-tc-coach hover:underline"
            data-testid="support-admin-back"
          >
            ← Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  const { loading, items, total } = state;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto space-y-6" data-testid="support-admin">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <Inbox className="w-6 h-6 text-tc-coach" /> Support submissions
          </h1>
          <p className="text-tc-sub mt-1 text-sm">
            Every message sent through the public Support form, newest first. Admin-only.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="support-admin-refresh"
            onClick={load}
            className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm text-tc-sub hover:border-white/30 flex items-center gap-2"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Refresh
          </button>
          <button
            data-testid="support-admin-export"
            onClick={downloadCsv}
            disabled={exporting || total === 0}
            className="rounded-lg bg-tc-coach/15 border border-tc-coach/40 text-tc-coach px-3 py-2 text-sm font-medium hover:bg-tc-coach/25 disabled:opacity-50 flex items-center gap-2"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Export CSV
          </button>
        </div>
      </header>

      <div className="tc-card p-4 flex items-center gap-4" data-testid="support-admin-summary">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-tc-muted">Total submissions</div>
          <div className="mt-1 text-2xl font-bold text-tc-txt">{total}</div>
        </div>
        <div className="w-px self-stretch bg-white/10 mx-2" />
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-tc-muted">Shown</div>
          <div className="mt-1 text-2xl font-bold text-tc-txt">{items.length}</div>
        </div>
      </div>

      <div className="tc-card p-0 overflow-hidden" data-testid="support-admin-list">
        {loading && items.length === 0 && (
          <div className="p-6 text-tc-muted font-mono text-sm">Loading submissions…</div>
        )}
        {!loading && items.length === 0 && (
          <div className="p-6 text-tc-muted text-sm" data-testid="support-admin-empty">
            No support submissions yet.
          </div>
        )}
        <ul className="divide-y divide-white/5">
          {items.map((m) => (
            <li key={m.id} className="p-4 sm:p-5" data-testid={`support-admin-item-${m.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-tc-txt">{m.name || "(no name)"}</span>
                    <a
                      href={`mailto:${m.email}`}
                      className="text-sm text-tc-coach hover:underline flex items-center gap-1"
                      data-testid={`support-admin-email-${m.id}`}
                    >
                      <Mail className="w-3.5 h-3.5" /> {m.email}
                    </a>
                  </div>
                  <div className="text-[11px] font-mono uppercase tracking-widest text-tc-muted mt-1">
                    {fmtDate(m.created_at)} · {m.ip || "no-ip"} · emailed:{" "}
                    <span className={m.emailed ? "text-tc-bull" : "text-tc-bear"}>{m.emailed ? "yes" : "no"}</span>
                  </div>
                </div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-tc-muted">
                  {m.status || "open"}
                </div>
              </div>
              <pre className="mt-3 whitespace-pre-wrap font-sans text-sm text-tc-sub bg-white/[0.02] border border-white/5 rounded-lg p-3">
                {m.message}
              </pre>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
